import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS } from '../designSystem';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch and search data
  const performSearch = async (query) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const lowerQuery = query.toLowerCase();

      // Fetch merchants and deals in parallel
      const [merchantRes, dealRes] = await Promise.all([
        axios.get(`${API_URL}/merchants?skip=0&limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/deals?skip=0&limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const merchants = merchantRes.data || [];
      const deals = dealRes.data?.deals || dealRes.data || [];

      // Filter results
      const merchantResults = merchants
        .filter(m =>
          m.legal_name?.toLowerCase().includes(lowerQuery) ||
          m.dba_name?.toLowerCase().includes(lowerQuery) ||
          m.ein?.toLowerCase().includes(lowerQuery)
        )
        .map(m => ({
          type: 'merchant',
          id: m.id,
          title: m.legal_name,
          subtitle: m.dba_name,
          icon: '👥',
        }));

      const dealResults = deals
        .filter(d =>
          d.visual_deal_id?.toLowerCase().includes(lowerQuery) ||
          d.id?.toString().includes(lowerQuery)
        )
        .map(d => ({
          type: 'deal',
          id: d.id,
          title: `#${d.visual_deal_id}`,
          subtitle: `Status: ${d.status}`,
          icon: '💰',
        }));

      const combined = [...merchantResults, ...dealResults].slice(0, 8);
      setResults(combined);
      setIsOpen(true);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  };

  // Handle result click
  const handleResultClick = (result) => {
    if (result.type === 'merchant') {
      navigate(`/merchants/${result.id}`);
    } else if (result.type === 'deal') {
      navigate(`/deals/${result.id}`);
    }
    setSearchQuery('');
    setResults([]);
    setIsOpen(false);
  };

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  return (
    <div ref={searchRef} style={{ position: 'relative', flex: '1 1 auto', maxWidth: '600px', minWidth: '280px' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {/* Magnifying glass icon */}
        <div style={{
          position: 'absolute',
          left: SPACING.MD,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: COLORS.TEXT_SECONDARY,
          pointerEvents: 'none',
        }}>
          🔍
        </div>

        <input
          type="text"
          placeholder="Search merchants, deals..."
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={() => searchQuery && setIsOpen(true)}
          style={{
            width: '100%',
            padding: `${SPACING.LG} ${SPACING.MD} ${SPACING.LG} ${SPACING.XXXL}`,
            border: `2px solid #115845`,
            borderRadius: RADIUS.DEFAULT,
            fontSize: TYPOGRAPHY.BODY.fontSize,
            fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
            backgroundColor: '#f6fdf9',
            color: COLORS.TEXT_PRIMARY,
            transition: 'all 0.2s ease',
            outline: 'none',
            height: '52px',
            boxSizing: 'border-box',
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
            }
          }}
        />

        {/* Loading indicator */}
        {isLoading && (
          <div style={{
            position: 'absolute',
            right: SPACING.MD,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '16px',
            color: COLORS.TEXT_SECONDARY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            ⟳
          </div>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && results.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#ffffff',
          border: `1px solid ${COLORS.BORDER_DEFAULT}`,
          borderTopColor: '#e0e0e0',
          borderRadius: `0 0 ${RADIUS.DEFAULT} ${RADIUS.DEFAULT}`,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          marginTop: '2px',
          zIndex: 1000,
          maxHeight: '360px',
          overflowY: 'auto',
        }}>
          {results.map((result, idx) => (
            <div
              key={`${result.type}-${result.id}`}
              onClick={() => handleResultClick(result)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACING.MD,
                padding: `${SPACING.MD} ${SPACING.LG}`,
                cursor: 'pointer',
                borderBottom: idx < results.length - 1 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none',
                backgroundColor: idx === 0 ? '#f6fdf9' : '#ffffff',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f6fdf9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              <span style={{ fontSize: '16px' }}>{result.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  ...TYPOGRAPHY.BODY_SM,
                  fontWeight: '500',
                  color: COLORS.TEXT_PRIMARY,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {result.title}
                </div>
                <div style={{
                  ...TYPOGRAPHY.BODY_SM,
                  color: COLORS.TEXT_SECONDARY,
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {result.subtitle}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {isOpen && !isLoading && searchQuery && results.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#ffffff',
          border: `1px solid ${COLORS.BORDER_DEFAULT}`,
          borderTopColor: '#e0e0e0',
          borderRadius: `0 0 ${RADIUS.DEFAULT} ${RADIUS.DEFAULT}`,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          marginTop: '2px',
          padding: `${SPACING.LG} ${SPACING.LG}`,
          textAlign: 'center',
          color: COLORS.TEXT_SECONDARY,
          fontSize: TYPOGRAPHY.BODY_SM.fontSize,
          zIndex: 1000,
        }}>
          No results found
        </div>
      )}
    </div>
  );
}
